import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { useCategories } from "@/hooks/useCategories";
import { useShowcasePhotos } from "@/hooks/useShowcasePhotos";
import { useSiteContent } from "@/hooks/useSiteContent";
import { ShowcaseHeroBackground } from "@/components/ShowcaseHeroBackground";
import { ShowcaseGallery } from "@/components/ShowcaseGallery";
import { DesignStudioSection } from "@/components/DesignStudioSection";

const CATEGORY_META = {
  bundles: {
    description: "Console + controller + cables packages",
    image: "/images/cat-bundles.jpg",
  },
  controllers: {
    description: "Refurbished original Xbox controllers",
    image: "/images/cat-controllers.jpg",
  },
  consoles: {
    description: "Custom modded original Xbox systems",
    image: "/images/cat-consoles.jpg",
  },
  cables: {
    description: "AV & power cables for original Xbox",
    image: "/images/cat-cables.jpg",
  },
  other: {
    description: "Accessories, parts & more",
    image: "/images/cat-other.jpg",
  },
};
const FALLBACK_IMAGE = "/images/cat-other.jpg";

export default function HomePage() {
  const { categories: _cats } = useCategories();
  const showcasePhotos = useShowcasePhotos();
  const content = useSiteContent();
  const CATEGORIES = _cats.map(c => ({
    id: c.key,
    label: c.label,
    description: c.description || CATEGORY_META[c.key]?.description || `${c.label} products`,
    image: c.image_url || CATEGORY_META[c.key]?.image || FALLBACK_IMAGE,
  }));
  return (
    <div>
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-[#1A1A1A] hidden lg:block"
        style={{ backgroundColor: "#050505" }}
      >
        {/* Background texture */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: `url("/images/hero-texture.jpg")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "linear-gradient(#39FF14 1px, transparent 1px), linear-gradient(90deg, #39FF14 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        {/* Admin-uploaded showcase photos (cross-fading collage) */}
        <ShowcaseHeroBackground intervalMs={7000} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32 lg:py-10">
          <div className="flex flex-col lg:flex-row items-start gap-12">
            <div className="hidden lg:flex flex-1 max-w-2xl">
              <div className="w-full">
              <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-6 animate-fade-in-up">
                {content.hero_eyebrow}
              </p>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-[#F2F2F2] leading-[0.9] mb-8 animate-fade-in-up stagger-1"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                {content.hero_line1}<br />
                <span className="text-[#39FF14]">{content.hero_line2}</span><br />
                {content.hero_line3}
              </h1>
              <p className="text-[#A1A1A1] text-base sm:text-lg max-w-md mb-8 animate-fade-in-up stagger-2">
                {content.hero_description}
              </p>
              {/* Inline showcase — tightens up the left column below the hero copy on desktop.
                  Only renders when admin has uploaded showcase photos. */}
              {showcasePhotos.length > 0 && (
                <div className="animate-fade-in-up stagger-3 max-w-md" data-testid="hero-inline-showcase">
                  <div className="flex items-center gap-3 mb-3">
                    <p className="font-mono uppercase tracking-[0.3em] text-[10px] font-bold text-[#39FF14]">In The Wild</p>
                    <span className="h-px bg-[#1A1A1A] flex-1" />
                    <p className="font-mono text-[10px] text-[#A1A1A1]/60">Live from the shop</p>
                  </div>
                  <ShowcaseGallery tileCount={4} compact />
                </div>
              )}
              </div>
            </div>
            <div className="hidden lg:block flex-1 max-w-lg animate-fade-in-up stagger-2">
              <div className="grid grid-cols-2">
                {CATEGORIES.map((cat) => (
                  <Link
                    key={cat.id}
                    to={cat.id === "design" ? "/design-request" : `/products?category=${cat.id}`}
                    data-testid={`hero-category-${cat.id}`}
                    className="group bg-[#0D0D0D] hover:bg-[#141414] transition-all duration-300 relative overflow-hidden border-[0.5px] border-[#1A1A1A]"
                  >
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={cat.image}
                        alt={cat.label}
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                      />
                    </div>
                    <div className="p-3 border-t border-[#1A1A1A] group-hover:border-[#39FF14] transition-colors duration-300">
                      <h3 className="text-sm font-bold text-[#F2F2F2] uppercase tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                        {cat.label}
                      </h3>
                      <p className="text-[#A1A1A1] text-xs mt-0.5">{cat.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile layout */}
      <section data-testid="categories-section" className="lg:hidden" style={{ backgroundColor: "#050505" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Original Xbox Specialists + Hero text */}
          <div className="relative pt-8 pb-6 -mx-4 sm:-mx-6 px-4 sm:px-6 overflow-hidden">
            <ShowcaseHeroBackground intervalMs={7000} />
            <div className="relative">
              <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-6 animate-fade-in-up">
                {content.hero_eyebrow}
              </p>
              <h1
                className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2] leading-[0.9] mb-8 animate-fade-in-up stagger-1"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                {content.hero_line1}<br />
                <span className="text-[#39FF14]">{content.hero_line2}</span><br />
                {content.hero_line3}
              </h1>
              <p className="text-[#A1A1A1] text-base sm:text-lg max-w-md animate-fade-in-up stagger-2">
                {content.hero_description}
              </p>
            </div>
          </div>

          {/* All categories stacked */}
          {CATEGORIES.map((cat) => (
            <Link
              key={cat.id}
              to={cat.id === "design" ? "/design-request" : `/products?category=${cat.id}`}
              data-testid={`category-${cat.id}`}
              className="group block bg-[#0D0D0D] hover:bg-[#141414] transition-all duration-300 overflow-hidden mb-[1px]"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img src={cat.image} alt={cat.label} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
              </div>
              <div className="p-6 border-t border-[#1A1A1A] group-hover:border-[#39FF14] transition-colors duration-300">
                <h3 className="text-lg font-bold text-[#F2F2F2] uppercase tracking-tight mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>{cat.label}</h3>
                <p className="text-[#A1A1A1] text-sm">{cat.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Design Studio — featured completed custom builds. Renders nothing if none featured. */}
      <DesignStudioSection />

      {/* Photo showcase — mobile only (desktop shows it inline in the hero left column). */}
      <div className="lg:hidden">
        <ShowcaseGallery tileCount={6} />
      </div>

      {/* CTA */}
      <section className="border-t border-b border-[#1A1A1A] py-16 sm:py-24" style={{ backgroundColor: "#0D0D0D" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-4">{content.cta_eyebrow}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tighter text-[#F2F2F2] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
            {content.cta_heading}
          </h2>
          <p className="text-[#A1A1A1] text-base max-w-lg mx-auto mb-8">
            {content.cta_description}
          </p>
          <Link to="/products">
            <Button
              data-testid="cta-shop-btn"
              className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 animate-pulse-green"
            >
              {content.cta_button_text}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
